import pino, { Logger } from 'pino';
// @ts-ignore
import interactionLedger from './utils/interactionLedger';
import { getCurrentLoggingConfig, shouldLog, LogLevel } from './utils/loggingConfig';

const { DRYNOTES_ENV } = process.env;

// Create logger factory function that respects logging configuration
function createLogger(): Logger {
  const config = getCurrentLoggingConfig();
  
  // Map our log levels to pino levels
  const pinoLevel = config.enabled ? config.level : 'silent';
  
  return pino({
    level: pinoLevel,
    browser: {
      asObject: true,
      transmit: {
        level: pinoLevel,
        send: (level, logEvent) => {
          // Check if this log level should be output
          const logLevelMap: Record<number, LogLevel> = {
            10: 'trace',
            20: 'debug',
            30: 'info',
            40: 'warn',
            50: 'error',
            60: 'error', // fatal maps to error
          };
          
          const currentConfig = getCurrentLoggingConfig();
          const eventLevel = logLevelMap[level as unknown as number] || 'info';
          
          // Skip if logging is disabled or level is too low
          if (!shouldLog(eventLevel, currentConfig)) {
            return;
          }

          // Construct log payload
          // logEvent structure: { ts: number, messages: any[], bindings: any[], level: { value: number, label: string } }
          
          // Handle common call patterns: logger.info('msg') or logger.info({obj}, 'msg')
          let msg = '';
          let data = {};

          const messages = logEvent.messages;
          if (messages.length > 0) {
              if (typeof messages[0] === 'string') {
                  msg = messages[0];
                  if (messages.length > 1 && typeof messages[1] === 'object') {
                      data = messages[1];
                  }
              } else if (typeof messages[0] === 'object') {
                  data = messages[0];
                  if (messages.length > 1 && typeof messages[1] === 'string') {
                      msg = messages[1];
                  } else if (data && (data as any).msg) {
                      msg = (data as any).msg;
                  }
              }
          }

          // Merge bindings (context)
          const context = logEvent.bindings.reduce((acc, curr) => ({ ...acc, ...curr }), {});
          
          const payload = {
            level,
            time: logEvent.ts,
            msg,
            ...context,
            ...data,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            url: typeof window !== 'undefined' ? window.location.href : undefined,
          };

          // Integrate with Interaction Ledger (Capture all logs in ledger)
          // Only if logging is enabled and console is enabled
          if (typeof window !== 'undefined' && interactionLedger && currentConfig.consoleEnabled) {
               // Avoid circular dependency or double logging if ledger uses logger (it doesn't seem to)
               interactionLedger.log(level, 'logger', payload, msg || 'Log Entry');
          }

          // Only send to server in production
          if (DRYNOTES_ENV !== 'production') return;

          // Send to API
          // Use sendBeacon if available for better reliability on unload, otherwise fetch
          const url = '/api/logs';
          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          
          if (navigator.sendBeacon) {
            navigator.sendBeacon(url, blob);
          } else {
            fetch(url, {
              method: 'POST',
              body: JSON.stringify(payload),
              headers: { 'Content-Type': 'application/json' }
            }).catch(console.error);
          }
        },
      },
    },
  });
}

// Create initial logger instance
let logger: Logger = createLogger();

// Function to update logger when configuration changes
export function updateLogger(userSettings?: any) {
  // Recreate logger with new configuration
  logger = createLogger();
  return logger;
}

export default logger;
